import type { User } from "../../../auth/queries";
import { hasAnyGlobalRole, hasTeamRole } from "../../../auth/queries";
import type { Team } from "../../../lib/queries/teams";
import type { Competition } from "../../../lib/queries/competitions";

// Which cards the dashboard composes, by role. Modelled on the NAV_GROUPS
// table in shell/Sidebar — one declarative place to answer „who sees what",
// so the page body stays a layout and the rules stay testable.
//
// Two derivations are deliberately different:
//
//   - `teams` is every team the user belongs to in any role, because a trenér
//     wants to see their team's readiness too.
//   - `leadTeams` is only the teams they lead AND that are active, because
//     those are the only ones rbac.CanManageTeam lets them write to — offering
//     „Přihlásit" on a pozastavený team would be a button that 403s.

export interface DashboardBlocks {
  isAdmin: boolean;
  teams: Team[];
  leadTeams: Team[];
  organizedCompetitions: Competition[];
  /** True when no role produced a single card — render the fallback. */
  isEmpty: boolean;
}

export function dashboardBlocks(
  me: User | null | undefined,
  teams: Team[] | undefined,
  competitions: Competition[] | undefined,
): DashboardBlocks {
  // Global office staff: an account with no team and no competition of its
  // own is not „not linked yet" when its work is the whole federation's.
  const isAdmin = hasAnyGlobalRole(
    me,
    "admin",
    "members_admin",
    "members_viewer",
    "competitions_admin",
    "competitions_viewer",
    "global_viewer",
  );
  const all = teams ?? [];
  const leadTeams = all.filter(
    (t) => t.status === "active" && hasTeamRole(me, t.id, "team_leader"),
  );
  // Competitions this person organizes personally. Deliberately not
  // canManageCompetition: that is true of every competition for an admin, and
  // „Moje soutěže" listing the whole calendar would say nothing.
  const organizedCompetitions = (competitions ?? []).filter((c) =>
    (me?.assignments ?? []).some(
      (a) =>
        a.role === "competition_organizer" &&
        a.scope_type === "competition" &&
        a.scope_id === c.id,
    ),
  );

  return {
    isAdmin,
    teams: all,
    leadTeams,
    organizedCompetitions,
    isEmpty: !isAdmin && all.length === 0 && organizedCompetitions.length === 0,
  };
}
